import { useEffect, useState } from "react";
import axios from "axios";
import { API_URL, getHeaders } from "../config";
import HLSPlayer from "./VideoPlayer";
import { postVideoHistory } from "../services/VideoHistory";
import { CircularProgressWithLabel } from "./CircularProgressWithLabel";
import { Video as VideoIcon } from "./icons";

/**
 * The video stage: player on the left, the lesson's other videos on the right.
 *
 * Layout only — playback, chapter markers and the history POST that feeds
 * completion are unchanged. The playlist entries dropped their duplicated
 * module thumbnail (every row showed the same picture) in favour of the one
 * thing that differs per row: how much of it the learner has watched.
 */
export const VideoSection = ({ ppt, selectedSection }) => {
  const [url, setUrl] = useState("");
  const [selectedVideoIndex, setSelectedVideoIndex] = useState(0);
  const [videoList, setVideoList] = useState([]);
  const [chapters, setChapters] = useState([]);

  useEffect(() => {
    if (!videoList?.length) return;
    (async () => {
      const { data } = await axios.get(
        `${API_URL}/api/video/chapters/${videoList[selectedVideoIndex]._id}`
      );
      setChapters(data?.data);
    })();
  }, [videoList]);

  useEffect(() => {
    if (!ppt) return;
    (async () => {
      try {
        if (ppt._id) {
          const { data } = await axios.get(`${API_URL}/api/video/url/${ppt._id}`, {
            headers: getHeaders().headers,
          });
          setVideoList(data?.data);
          setUrl(`${API_URL}/api/video/stream?key=${data?.data[0]?.url}`);
        } else {
          setVideoList([]);
          setUrl("");
        }
      } catch (e) {
        console.log("Error while fetching");
        setVideoList([]);
        setUrl("");
      }
    })();
  }, [ppt]);

  const handleSelectVideo = (index) => {
    setSelectedVideoIndex(index);
    setUrl(`${API_URL}/api/video/stream?key=${videoList[index].url}`);
  };

  const postHistory = async (time, videoTime, totalTime) => {
    await postVideoHistory(
      ppt.title,
      time,
      videoTime,
      totalTime,
      videoList[selectedVideoIndex]._id
    );
  };

  if (!videoList.length) {
    return (
      <div className="ds-empty" style={{ minHeight: 260 }}>
        <span className="ds-empty__icon">
          <VideoIcon size={20} />
        </span>
        <p className="ds-empty__text">
          No video has been published for this lesson yet.
        </p>
      </div>
    );
  }

  const hasPlaylist = videoList.length > 1;

  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: hasPlaylist ? "minmax(0,1fr) 320px" : "minmax(0,1fr)",
        gap: "var(--s-4)",
        padding: "var(--s-5)",
      }}
    >
      <div
        style={{
          borderRadius: "var(--r-lg)",
          overflow: "hidden",
          background: "#0b0d18",
          minHeight: 320,
          display: "flex",
        }}
      >
        <HLSPlayer
          videoUrl={url}
          markers={chapters}
          postHistory={postHistory}
          selectedSection={selectedSection}
          videoId={videoList[selectedVideoIndex]._id}
        />
      </div>

      {hasPlaylist && (
        <div className="ds-scroll" style={{ maxHeight: 460, overflowY: "auto" }}>
          <p className="ds-eyebrow" style={{ marginBottom: "var(--s-3)" }}>
            In this lesson
          </p>
          {videoList.map((videoItem, index) => {
            const isActive = index === selectedVideoIndex;
            return (
              <button
                key={videoItem._id}
                type="button"
                onClick={() => handleSelectVideo(index)}
                className="ds-card"
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "var(--s-3)",
                  width: "100%",
                  textAlign: "left",
                  padding: "var(--s-3)",
                  marginBottom: "var(--s-2)",
                  cursor: "pointer",
                  font: "inherit",
                  background: isActive ? "var(--brand-50)" : "var(--surface)",
                  borderColor: isActive ? "var(--brand-100)" : "var(--border)",
                }}
              >
                <span
                  className="ds-tile"
                  style={{
                    width: 30,
                    height: 30,
                    background: isActive ? "var(--brand-600)" : "var(--surface-sunken)",
                    color: isActive ? "#fff" : "var(--ink-3)",
                  }}
                >
                  <VideoIcon size={15} />
                </span>
                <span
                  className="ds-truncate ds-grow"
                  style={{
                    fontSize: 13,
                    fontWeight: isActive ? 650 : 550,
                    color: isActive ? "var(--brand-700)" : "var(--ink)",
                  }}
                  title={videoItem.name}
                >
                  {videoItem.name}
                </span>
                <CircularProgressWithLabel
                  value={videoItem?.VideoHistory?.percentage || 0}
                />
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default VideoSection;
